/* eslint-disable no-promise-executor-return */
/* eslint-disable max-lines-per-function */
import {
	Box,
	Button,
	Flex,
	Image,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spinner,
	Text,
	VStack
} from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';

import { ComponentController as BpController, RtaBlueprintComponent } from '@rta/rta-blueprint-component';
import { Kitchen3dController as TdController } from '@rta/rta-blueprint-3d-component';

import { RenderingPreviewSurface, type PreviewCapture } from '@components/ui';

import type { Project } from '@state';

type FavoriteConfirmationModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (overheadImage: string, threeDImage: string) => void;
	designName: string;
	deal: Project | null;
	bpController: BpController | null;
	tdController: TdController | null;
	savedRenderingUrl: string;
};

const captureOverheadFromContainer = (container: HTMLDivElement): Promise<string | null> => {
	return new Promise((resolve) => {
		const svgEl = container.querySelector('svg');
		if (!svgEl) { resolve(null); return; }

		const serialized = new XMLSerializer().serializeToString(svgEl);
		const svgBlob = new Blob([serialized], { type: 'image/svg+xml;charset=utf-8' });
		const url = URL.createObjectURL(svgBlob);

		const img = new window.Image();
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = svgEl.clientWidth || 800;
			canvas.height = svgEl.clientHeight || 600;
			const ctx = canvas.getContext('2d');
			if (ctx) {
				ctx.fillStyle = '#ffffff';
				ctx.fillRect(0, 0, canvas.width, canvas.height);
				ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
			}
			URL.revokeObjectURL(url);
			resolve(canvas.toDataURL('image/png'));
		};
		img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
		img.src = url;
	});
};

const stripBase64Prefix = (dataUrl: string): string => {
	const commaIndex = dataUrl.indexOf(',');
	return commaIndex >= 0 ? dataUrl.substring(commaIndex + 1) : dataUrl;
};

export const FavoriteConfirmationModal = (props: FavoriteConfirmationModalProps): JSX.Element => {
	const {
		isOpen,
		onClose,
		onConfirm,
		designName,
		deal,
		bpController,
		tdController,
		savedRenderingUrl
	} = props;

	const [isSending, setIsSending] = useState(false);
	const overheadContainerRef = useRef<HTMLDivElement>(null);

	// The saved rendering is what uploads, shown as a static image. Only when a design has no
	// rendering at all (reached via "Continue Anyway") does a live pane appear to frame one.
	const [capture, setCapture] = useState<PreviewCapture | null>(null);
	const threeDPreview = savedRenderingUrl || null;

	useEffect(() => {
		if (!isOpen) setIsSending(false);
	}, [isOpen]);

	const handleConfirm = useCallback(async (): Promise<void> => {
		setIsSending(true);

		// Capture overhead from the live SVG in the modal
		let overheadDataUrl: string | null = null;
		if (overheadContainerRef.current) {
			overheadDataUrl = await captureOverheadFromContainer(overheadContainerRef.current);
		}

		const threeDSource = threeDPreview ?? (capture ? await capture(2) : null);

		if (!overheadDataUrl || !threeDSource) {
			console.error('Failed to capture one or both images', { overheadDataUrl: !!overheadDataUrl, threeDSource: !!threeDSource });
			setIsSending(false);
			return;
		}

		const threeDPayload = /^https?:\/\//i.test(threeDSource) ? threeDSource : stripBase64Prefix(threeDSource);
		onConfirm(
			stripBase64Prefix(overheadDataUrl),
			threeDPayload
		);
	}, [capture, threeDPreview, onConfirm]);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="4xl" closeOnOverlayClick={!isSending}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Favorite Design</ModalHeader>
				<ModalCloseButton isDisabled={isSending} />
				<ModalBody>
					<VStack spacing={4} align="stretch">
						<Text fontSize="sm">
							Favoriting <strong>{designName}</strong> will upload design images to the associated HubSpot deal.
						</Text>

						{deal && (
							<Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
								<Text fontSize="sm" fontWeight="bold">{deal.dealName || deal.dealId}</Text>
								<Text fontSize="xs" color="gray.600">
									Deal ID: {deal.dealId}
								</Text>
								{deal.address && (
									<Text fontSize="xs" color="gray.600">
										{[deal.address, deal.city, deal.state, deal.zip].filter(Boolean).join(', ')}
									</Text>
								)}
							</Box>
						)}

						<Flex gap={4} justify="center">
							<VStack flex={1} spacing={1}>
								<Text fontSize="xs" fontWeight="bold" color="gray.600">Overhead View</Text>
								<Box
									ref={overheadContainerRef}
									borderWidth="1px"
									borderRadius="md"
									overflow="hidden"
									bg="white"
									w="100%"
									h="350px"
									position="relative"
									sx={{ '& [style*="pointer-events: none"][style*="position: absolute"]': { display: 'none !important' } }}
								>
									{isOpen && bpController && (
										<RtaBlueprintComponent
											controller={bpController}
											backgroundColor="white"
										/>
									)}
								</Box>
							</VStack>

							<VStack flex={1} spacing={1}>
								<Text fontSize="xs" fontWeight="bold" color="gray.600">
									{threeDPreview ? ' 3D View — saved rendering' : '3D View — drag to frame the shot'}
								</Text>
								{threeDPreview ? (
									<Box borderWidth="1px" borderRadius="md" overflow="hidden" bg="gray.900" w="100%" h="350px">
										<Image src={threeDPreview} alt="3D view" objectFit="contain" w="100%" h="100%" bg="gray.900" />
									</Box>
								) : (
									<>
										<Box w="100%" borderWidth="1px" borderRadius="md" overflow="hidden">
											<RenderingPreviewSurface
												tdController={tdController}
												active={isOpen}
												onCaptureReady={(fn) => setCapture(() => fn)}
											/>
										</Box>
										{!capture && (
											<Flex align="center" gap={2}>
												<Spinner size="xs" color="gray.400" />
												<Text fontSize="xs" color="gray.500">Preparing 3D…</Text>
											</Flex>
										)}
									</>
								)}
							</VStack>
						</Flex>
					</VStack>
				</ModalBody>
				<ModalFooter>
					<Button variant="ghost" mr={3} onClick={onClose} isDisabled={isSending}>
						Cancel
					</Button>
					<Button
						colorScheme="blue"
						onClick={handleConfirm}
						isDisabled={isSending || (!threeDPreview && !capture)}
						isLoading={isSending}
						loadingText="Sending..."
					>
						Confirm & Send
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
